import React, { useState, useEffect } from "react";
import { Row, Col, Card, CardBody, Input, Label, Button, Form, Alert } from "reactstrap";
import { get, put } from "../../helpers/api_helper";
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { Trans } from 'react-i18next';
import { useNavigate, useParams } from "react-router-dom";
import Loader from "common/loader";
import UploadImage from "common/uploadImage";
import ShipmentTypesSelect from "common/shipmentTypesSelect";
import { SHOW_ITEM, UPDATE_ITEM } from "helpers/api_endpoints";

document.title = "Edit Item";

const EditItem = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState('');
    const [shipmentTypeIds, setShipmentTypeIds] = useState([]);
    const [formData, setFormData] = useState({
        type: "1",
        name: '',
        status: 1,
        weight: '',
        price: '',
        symbol: '',
        description: '',
        dimensions: '',
        image: '',
        attributes: ''
    });

    useEffect(() => {
        const fetchItem = async () => {
            try {
                const response = await get(`${SHOW_ITEM}/${id}`);
                const item = response.data;

                setFormData({
                    type: item.type || "1",
                    name: item.name || '',
                    status: item.status || 1,
                    weight: item.weight || '',
                    price: item.price || 0,
                    symbol: item.symbol || '',
                    description: item.description || '',
                    dimensions: item.dimensions || '',
                    image: item.image || '',
                    attributes: item.attributes || ''
                });
                setShipmentTypeIds((item.ShipmentTypes || []).map(type => type.id));

                setLoading(false);
            } catch (error) {
                console.error('Error fetching item:', error);
                navigate('/items', {
                    state: { errorMsg: 'Failed to load item data' }
                });
            }
        };

        fetchItem();
    }, [id, navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrorMsg('');

        try {
            // Validate required string fields
            const requiredStringFields = { name: formData.name, symbol: formData.symbol };
            for (const [key, value] of Object.entries(requiredStringFields)) {
                if (!value || typeof value !== 'string') {
                    setErrorMsg(`${key} is required and must be a string.`);
                    setLoading(false);
                    return;
                }
            }

            // Validate weight if provided
            if (formData.weight && isNaN(Number(formData.weight))) {
                setErrorMsg('weight must be a valid number.');
                setLoading(false);
                return;
            }

            // Validate optional string fields
            const optionalStringFields = {
                description: formData.description,
                dimensions: formData.dimensions,
                image: formData.image,
                attributes: formData.attributes
            };

            for (const [key, value] of Object.entries(optionalStringFields)) {
                if (value !== '' && typeof value !== 'string') {
                    setErrorMsg(`${key} must be a string if provided.`);
                    setLoading(false);
                    return;
                }
            }

            const imagePath = document.getElementById('imagePath')?.value || formData.image;

            const itemData = {
                id: Number(id),
                name: formData.name,
                status: Number(formData.status),
              weight: formData.weight ? Number(formData.weight) : null,
                price: formData.price ? Number(formData.price) : null,
                symbol: formData.symbol,
                description: formData.description || null,
                dimensions: formData.dimensions || null,
                image: imagePath || null,
                attributes: formData.attributes || null,
                shipmentTypeIds
            };

            const response = await put(UPDATE_ITEM, itemData);

            if (response.code === 200) {
                navigate('/items', {
                    state: { successMsg: 'Item Updated Successfully' }
                });
            }
        } catch (error) {
            console.error('Update Item Error:', error);
            setErrorMsg(
                error?.response?.data?.message || 'Item Update Failed'
            );
        } finally {
            setLoading(false);
        }
    };

    const handleToggleStatus = () => {
        setFormData(prev => ({
            ...prev,
            status: prev.status === 1 ? 0 : 1
        }));
    };

    if (loading) {
        return <Loader />;
    }

    return (
        <React.Fragment>
            <div className="page-content">
                <div className="container-fluid">
                    <Breadcrumbs title="Items" breadcrumbItem="Edit Item" />
                    <Form onSubmit={handleSubmit}>
                        <Row>
                            <Col xl={12}>
                                <Card>
                                    <CardBody>
                                        {errorMsg && <Alert color="danger">{errorMsg}</Alert>}

                                        <Row>
                                            <div className="form-group col-md-4 mt-2">
                                                <Label className="form-label">
                                                    <Trans>Name</Trans>
                                                </Label>
                                                <Input
                                                    name="name"
                                                    value={formData.name}
                                                    className="form-control"
                                                    placeholder="Enter Name"
                                                    type="text"
                                                    onChange={handleChange}
                                                    required
                                                />
                                            </div>

                                            <div className="form-group col-md-4 mt-2">
                                                <Label className="form-label">
                                                    <Trans>Symbol</Trans>
                                                </Label>
                                                <Input
                                                    name="symbol"
                                                    value={formData.symbol}
                                                    className="form-control"
                                                    placeholder="Enter Symbol"
                                                    type="text"
                                                    onChange={handleChange}
                                                    required
                                                />
                                            </div>

                                            <div className="form-group col-md-4 mt-2">
                                                <Label className="form-label">
                                                    <Trans>Attributes</Trans>
                                                </Label>
                                                <Input
                                                    name="attributes"
                                                    value={formData.attributes}
                                                    className="form-control"
                                                    placeholder="Enter Attributes"
                                                    type="text"
                                                    onChange={handleChange}
                                                />
                                            </div>

                                            <div className="form-group col-md-4 mt-2">
                                                <Label className="form-label">
                                                    <Trans>Weight</Trans>
                                                </Label>
                                                <Input
                                                    name="weight"
                                                    value={formData.weight}
                                                    className="form-control"
                                                    placeholder="Enter Weight"
                                                    type="number"
                                                    onChange={handleChange}
                                                />
                                            </div>

                                            <div className="form-group col-md-4 mt-2">
                                                <Label className="form-label">
                                                    <Trans>Price</Trans>
                                                </Label>
                                                <Input
                                                    name="price"
                                                    value={formData.price}
                                                    className="form-control"
                                                    placeholder="Enter Price"
                                                    type="number"
                                                    onChange={handleChange}
                                                />
                                            </div>

                                            <div className="form-group col-md-4 mt-2">
                                                <Label className="form-label">
                                                    <Trans>Dimensions</Trans>
                                                </Label>
                                                <Input
                                                    name="dimensions"
                                                    value={formData.dimensions}
                                                    className="form-control"
                                                    placeholder="Enter Dimensions"
                                                    type="text"
                                                    onChange={handleChange}
                                                />
                                            </div>

                                            <div className="form-group col-md-12 mt-2">
                                                <Label className="form-label">
                                                    <Trans>Description</Trans>
                                                </Label>
                                                <Input
                                                    name="description"
                                                    value={formData.description}
                                                    className="form-control"
                                                    placeholder="Enter Description"
                                                    type="textarea"
                                                    onChange={handleChange}
                                                />
                                            </div>

                                            <ShipmentTypesSelect
                                                value={shipmentTypeIds}
                                                onChange={setShipmentTypeIds}
                                            />

                                            <UploadImage
                                                state={{
                                                    InputLabel: 'Image',
                                                    filePath: formData.image,
                                                    type: 'items',
                                                    InputWidth: '4'
                                                }}
                                            />

                                            <div className="form-group col-md-2 mt-2">
                                                <Label className="form-label">
                                                    <Trans>Status</Trans>
                                                </Label>
                                                <div className="form-check form-check-end">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        checked={formData.status == 1}
                                                        onChange={handleToggleStatus}
                                                        id="statusCheckbox"
                                                    />
                                                    <label
                                                        className="form-check-label"
                                                        htmlFor="statusCheckbox"
                                                    >
                                                        <Trans>Active</Trans>
                                                    </label>
                                                </div>
                                            </div>
                                        </Row>

                                        <div className="mt-4">
                                            <Button
                                                type="submit"
                                                color="primary"
                                                disabled={loading}
                                            >
                                                {loading ? <Trans>Processing...</Trans> : <Trans>Update</Trans>}
                                            </Button>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    </Form>
                </div>
            </div>
        </React.Fragment>
    );
};

export default EditItem;
